import {
  AppstoreOutlined,
  ArrowLeftOutlined,
  BarsOutlined,
  DeleteOutlined,
  FolderAddOutlined,
  PictureOutlined,
  UndoOutlined
} from "@ant-design/icons"
import {
  Alert,
  App,
  Breadcrumb,
  Button,
  Card,
  Empty,
  Flex,
  Input,
  Radio,
  Skeleton,
  Spin,
  Tooltip,
  Typography
} from "antd"
import type { MenuItemType } from "antd/es/menu/interface"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router"
import { useLocation, useSearchParams } from "react-router-dom"

import { selectionResetToken } from "../../../../../../components/EntityTable/use-row-selection.ts"
import { RoutesPaths } from "../../../../../../constants/routes.ts"
import { useAppDispatch } from "../../../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../../../hooks/use-app-selector.ts"
import { useDateFormatter } from "../../../../../../hooks/use-date-formatter.tsx"
import { useAlbumActionsController } from "../../../../../Album/hooks/useAlbumActionsController.tsx"
import { createAlbum } from "../../../../../Album/redux/album.thunks.ts"
import type {
  AlbumCreateData,
  AlbumListItem
} from "../../../../../Album/types/album.types.ts"
import { useSendForSignature } from "../../../../../Esign/hooks/use-send-for-signature.ts"
import { ActivityDrawer } from "../../../../../Files/components/ActivityDrawer.tsx"
import { AssignDocumentTypeModal } from "../../../../../Files/components/AssignDocumentTypeModal.tsx"
import { BulkActionBar } from "../../../../../Files/components/BulkActionBar.tsx"
import { CommentsDrawer } from "../../../../../Files/components/CommentsDrawer.tsx"
import { CreateFolderCard } from "../../../../../Files/components/CreateFolderCard.tsx"
import { CreateFolderRow } from "../../../../../Files/components/CreateFolderRow.tsx"
import { FileCard } from "../../../../../Files/components/FileCard.tsx"
import { FileViewer } from "../../../../../Files/components/FileViewer.tsx"
import {
  FilesActiveFilters,
  type FilterLabelLookup
} from "../../../../../Files/components/FilesActiveFilters.tsx"
import { FilesFilterDrawer } from "../../../../../Files/components/FilesFilterDrawer.tsx"
import { FilesListView } from "../../../../../Files/components/FilesListView.tsx"
import { FilesSidebar } from "../../../../../Files/components/FilesSidebar.tsx"
import { FilesSortSelect } from "../../../../../Files/components/FilesSortSelect.tsx"
import { FilesUploadMenu } from "../../../../../Files/components/FilesUploadMenu.tsx"
import { FolderCard } from "../../../../../Files/components/FolderCard.tsx"
import { LinkRecordModal } from "../../../../../Files/components/LinkRecordModal.tsx"
import { MoveFileModal } from "../../../../../Files/components/MoveFileModal.tsx"
import { MoveFolderModal } from "../../../../../Files/components/MoveFolderModal.tsx"
import { NewFolderModal } from "../../../../../Files/components/NewFolderModal.tsx"
import { RejectReviewModal } from "../../../../../Files/components/RejectReviewModal.tsx"
import { RequestReviewModal } from "../../../../../Files/components/RequestReviewModal.tsx"
import { ReviewDrawer } from "../../../../../Files/components/ReviewDrawer.tsx"
import { ShareDialog } from "../../../../../Files/components/ShareDialog.tsx"
import { UploadNewVersionModal } from "../../../../../Files/components/UploadNewVersionModal.tsx"
import { UploadQueuePanel } from "../../../../../Files/components/UploadQueuePanel.tsx"
import { VersionHistoryDrawer } from "../../../../../Files/components/VersionHistoryDrawer.tsx"
import { containersInFilteredResult } from "../../../../../Files/helpers/containers-in-filtered-result.ts"
import { useFilesView } from "../../../../../Files/helpers/files-view-preference.ts"
import {
  fileItemKey,
  mapDocumentToFileItem
} from "../../../../../Files/helpers/map-to-file-item.ts"
import { readFileDate } from "../../../../../Files/helpers/read-file-date.ts"
import {
  sortFileItems,
  sortFolders
} from "../../../../../Files/helpers/sort-file-items.ts"
import { useFavorites } from "../../../../../Files/hooks/useFavorites.ts"
import { useFileActions } from "../../../../../Files/hooks/useFileActions.ts"
import { useFilesData } from "../../../../../Files/hooks/useFilesData.ts"
import { useTrash } from "../../../../../Files/hooks/useTrash.ts"
import { useUploadQueue } from "../../../../../Files/hooks/useUploadQueue.ts"
import { type FileItem } from "../../../../../Files/types/file-item.types.ts"
import {
  DEFAULT_SORT_ORDER,
  EMPTY_FILES_FILTERS,
  type FilesSortOrder,
  countActiveFilters
} from "../../../../../Files/types/files-filters.types.ts"
import { type FilesNav } from "../../../../../Files/types/files-nav.types.ts"
import { type FolderListItem } from "../../../../../Files/types/folder.types.ts"
import type {
  ProjectDocument,
  RelatedRecord
} from "../../../../types/project-documents.types.ts"
import { ProjectFileAddToAlbumModal } from "../../ProjectFileAddToAlbumModal.tsx"
import { RenameFileModal } from "../../RenameFileModal.tsx"
import { AlbumCard } from "../ProjectViewAlbums/AlbumCard/AlbumCard.tsx"
import { CreateAlbumModal } from "../ProjectViewAlbums/CreateAlbum/CreateAlbumModal.tsx"
import ProjectViewAlbumsSection from "../ProjectViewAlbums/ProjectViewAlbumsSection.tsx"
import { FilterDrawerTrigger } from "../ProjectViewAllFiles/ProjectAllFiles/ProjectAllFilesFilterDrawer.tsx"
import { UploadDocumentModal } from "../ProjectViewDocuments/UploadDocumentModal.tsx"

const { Text, Title, Paragraph } = Typography

const RECENT_LIMIT = 10
const FOLDERS_PREVIEW_LIMIT = 8
const FOLDER_LOADER_DELAY_MS = 1000

const NAV_VALUES: FilesNav[] = ["all", "recent", "folders", "albums"]

const readInitialNav = (value: string | null): FilesNav =>
  NAV_VALUES.includes(value as FilesNav) ? (value as FilesNav) : "all"

function FilesSectionCard({
  title,
  action,
  children
}: {
  title: string
  action?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <Flex vertical gap="small">
      <Flex align="center" justify="space-between">
        <Title level={5} className="!mb-0">
          {title}
        </Title>
        {action}
      </Flex>
      {children}
    </Flex>
  )
}

function ProjectViewFilesSection() {
  const { t } = useTranslation()
  const { modal, message } = App.useApp()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const projectId = useParams()?.id as string
  // Set only by Global Map's `QuickViewFiles` "View all files" link — see
  // its own comment for why a plain browser Back can't get the user home.
  const fromGlobalMap =
    (location.state as { fromGlobalMap?: string } | null)?.fromGlobalMap ?? null
  const currentUserId = useAppSelector((state) => state.auth?.userInfo?.id)
  const projectName =
    useAppSelector((state) => state.project?.project?.lead_name) ?? ""
  const moduleDocs = useAppSelector(
    (state) => state.auth?.permissions?.codes?.projects?.docs
  )
  const albumsCount = useAppSelector((state) => state.album?.albums?.count ?? 0)
  const sendForSignature = useSendForSignature(projectId)
  const [searchParams, setSearchParams] = useSearchParams()
  const focusFileId = searchParams.get("fileId")
  const [isCreateAlbumOpen, setIsCreateAlbumOpen] = useState(false)

  const createAllowed = !!moduleDocs?.project_doc_create
  const albumCreateAllowed = !!moduleDocs?.project_album_create

  const rights = useMemo(() => {
    const editAllowed = !!moduleDocs?.project_doc_edit
    const deleteAllowed = !!moduleDocs?.project_doc_delete
    const manageOthers = !!moduleDocs?.project_doc_manage_others
    return {
      currentUserId,
      createAllowed,
      editAllowed,
      editForeignAllowed: editAllowed && manageOthers,
      deleteAllowed,
      deleteForeignAllowed: deleteAllowed && manageOthers,
      manageOthersAllowed: manageOthers
    }
  }, [moduleDocs, currentUserId, createAllowed])

  const data = useFilesData({
    projectId,
    initialNav: readInitialNav(searchParams.get("nav")),
    rights
  })

  const handleCreateAlbum = async (albumData: AlbumCreateData) => {
    // `.unwrap()` is what makes a refusal reject: the modal keeps itself open
    // when its onSubmit rejects, and a bare dispatch resolves either way,
    // closing over the name the server just asked the user to change. The
    // thunk has already surfaced the reason through the errors slice.
    await dispatch(
      createAlbum({ ...albumData, project: projectId, order: albumsCount + 1 })
    ).unwrap()
    await data.reload()
  }

  const sendFileForSignature = useMemo(
    () =>
      sendForSignature
        ? (file: FileItem) =>
            sendForSignature({ type: "attachment", id: file.id })
        : undefined,
    [sendForSignature]
  )

  const actions = useFileActions({
    projectId,
    currentFolderId: data.currentFolderId,
    fileItems: data.fileItems,
    rights,
    reload: data.reload,
    onSendForSignature: sendFileForSignature
  })
  const trash = useTrash({ projectId, rights, enabled: data.nav === "trash" })

  // A selection made on one screen must not be acted on from another.
  const selectionScope = selectionResetToken({
    nav: data.nav,
    folder: data.currentFolderId,
    search: data.search.trim(),
    filters: data.filters
  })
  const { clearSelection } = actions
  useEffect(() => {
    clearSelection()
  }, [selectionScope, clearSelection])

  const trashFileActions = useCallback(
    (file: FileItem): MenuItemType[] => [
      {
        key: "restore",
        icon: <UndoOutlined />,
        label: t("files.actions.restore"),
        onClick: () =>
          void actions.restoreFile(file).then(async (ok) => {
            if (ok) await trash.reload()
          })
      },
      {
        key: "permanent-delete",
        icon: <DeleteOutlined />,
        danger: true,
        label: t("files.actions.permanentDelete"),
        onClick: () =>
          modal.confirm({
            centered: true,
            title: t("files.permanentDelete.title"),
            content: t("files.permanentDelete.body"),
            okText: t("files.permanentDelete.confirm"),
            okButtonProps: { danger: true },
            onOk: async () => {
              const ok = await actions.permanentlyDeleteFile(file)
              if (ok) await trash.reload()
            }
          })
      }
    ],
    [t, modal, actions, trash]
  )

  const handleEmptyTrash = useCallback(() => {
    modal.confirm({
      centered: true,
      title: t("files.emptyTrash.title"),
      content: t("files.emptyTrash.body"),
      okText: t("files.emptyTrash.confirm"),
      okButtonProps: { danger: true },
      onOk: async () => {
        const ok = await actions.emptyTrash()
        if (ok) await trash.reload()
      }
    })
  }, [t, modal, actions, trash])

  const [uploadQueueOpen, setUploadQueueOpen] = useState(false)
  const uploadQueue = useUploadQueue({
    projectId,
    folderId: data.currentFolderId,
    onUploaded: data.reload
  })
  const enqueueUploads = useCallback(
    (files: File[]) => {
      if (!files.length) return
      uploadQueue.enqueue(files)
      setUploadQueueOpen(true)
    },
    [uploadQueue]
  )
  const [dragActive, setDragActive] = useState(false)
  const dragCounter = useRef(0)
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    if (!createAllowed || !e.dataTransfer.types.includes("Files")) return
    dragCounter.current += 1
    setDragActive(true)
  }
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    dragCounter.current = Math.max(0, dragCounter.current - 1)
    if (dragCounter.current === 0) setDragActive(false)
  }
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    dragCounter.current = 0
    setDragActive(false)
    if (!createAllowed) return
    const files = Array.from(e.dataTransfer.files)
    enqueueUploads(files)
  }

  const [view, setView] = useFilesView()
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
  const [previewContext, setPreviewContext] = useState<FileItem[]>([])

  // `?fileId=` is a one-time instruction (e.g. from "View in Files" on a
  // Document Review) — find it, open it, then strip the param so revisiting
  // this tab later never keeps re-opening the same file. Documents are
  // paginated, so a match beyond the first page means loading more before
  // concluding the file isn't there at all.
  const resolvedFocusFileId = useRef<string | null>(null)
  useEffect(() => {
    if (!focusFileId || resolvedFocusFileId.current === focusFileId) return
    const match = data.fileItems.find((item) => item.id === focusFileId)
    const clearFocusFileId = () =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete("fileId")
          return next
        },
        { replace: true }
      )
    if (match) {
      resolvedFocusFileId.current = focusFileId
      setPreviewContext([match])
      actions.setPreviewFile(match)
      clearFocusFileId()
      return
    }
    if (data.hasMore) {
      data.loadMoreDocuments()
      return
    }
    if (data.hasLoaded) {
      resolvedFocusFileId.current = focusFileId
      message.warning(t("files.deepLink.notFound"))
      clearFocusFileId()
    }
  }, [focusFileId, data.fileItems, data.hasMore, data.hasLoaded])
  const [sortOrder, setSortOrder] = useState<FilesSortOrder>(DEFAULT_SORT_ORDER)
  const [filterLabels, setFilterLabels] = useState<FilterLabelLookup>({
    fileTypes: [],
    folders: [],
    albums: [],
    documentTypes: [],
    statuses: [],
    modelTypes: [],
    customers: []
  })
  const favorites = useFavorites(projectId)
  const albumActionsController = useAlbumActionsController({
    projectId,
    userId: currentUserId,
    editAllowed: rights.editAllowed,
    deleteAllowed: rights.deleteAllowed,
    deleteForeignAllowed: rights.deleteForeignAllowed,
    isFavorite: favorites.isFavorite,
    toggleFavorite: favorites.toggleFavorite,
    onChanged: data.reload
  })
  const albumMenuItemsFor = useCallback(
    (album: AlbumListItem) =>
      albumActionsController.menuItemsFor(
        album,
        (a) => navigate(RoutesPaths.project.view(projectId).album(a.id)),
        t
      ),
    [albumActionsController, navigate, projectId, t]
  )

  const { nav, setNav, fileItems, currentFolderId, isSearching } = data

  // "Shared by customer" is a filtered view of the existing All Files
  // listing (customer uploads + customer-created containers), not a
  // separate destination — so it is All Files with both origin filters
  // preset, and its nav highlight is derived from that exact combination
  // rather than tracked as its own nav state.
  const isSharedByCustomerActive =
    nav === "all" &&
    !isSearching &&
    data.filters.uploaded_by_origin === "customer" &&
    data.filters.created_by_origin === "customer"
  const activateSharedByCustomer = useCallback(() => {
    setNav("all")
    data.setFilters((current) => ({
      ...current,
      uploaded_by_origin: "customer",
      created_by_origin: "customer"
    }))
  }, [setNav, data])

  const { dateFormat, timezone } = useDateFormatter()
  const parseSortDate = useCallback(
    (value: string) => readFileDate(value, { dateFormat, timezone }) ?? 0,
    [dateFormat, timezone]
  )
  const sortedFileItems = useMemo(
    () => sortFileItems(fileItems, sortOrder, parseSortDate),
    [fileItems, sortOrder, parseSortDate]
  )
  const sortedFolders = useMemo(
    () => sortFolders(data.folders, sortOrder, parseSortDate),
    [data.folders, sortOrder, parseSortDate]
  )
  // Workflow actions (request/approve/reject review, change status) reload
  // the list but never touch the File Viewer's own `previewFile`/`previewContext`
  // snapshots — without this they keep showing the pre-action status until
  // closed and reopened. Matches by key (not identity), so a same-file reload
  // always hands the viewer the freshest row, on whichever page it's open to.
  useEffect(() => {
    if (!actions.previewFile) return
    const fresh = sortedFileItems.find(
      (item) => item.key === actions.previewFile?.key
    )
    if (!fresh || fresh === actions.previewFile) return
    actions.setPreviewFile(fresh)
    setPreviewContext((items) =>
      items.map(
        (item) => sortedFileItems.find((f) => f.key === item.key) ?? item
      )
    )
  }, [sortedFileItems, actions.previewFile, actions.setPreviewFile])

  const recentItems = sortedFileItems.slice(0, RECENT_LIMIT)
  const favoriteItems = useMemo(
    () => sortedFileItems.filter((file) => favorites.isFavorite(file.key)),
    [sortedFileItems, favorites]
  )
  const activeFilterCount = countActiveFilters(data.filters)
  const owners = useMemo(
    () =>
      [
        ...new Set(
          fileItems
            .map((file) => file.createdByName)
            .filter((name): name is string => !!name)
        )
      ].sort(),
    [fileItems]
  )

  const isEmptyProject =
    data.hasLoaded &&
    !isSearching &&
    !data.hasActiveFilter &&
    nav === "all" &&
    !fileItems.length &&
    !sortedFolders.length &&
    !data.albumsPreview.length

  const handlePreviewVersion = (document: ProjectDocument) => {
    const item = mapDocumentToFileItem(document, rights)
    setPreviewContext([item])
    actions.setPreviewFile(item)
  }

  // The File Viewer keeps whatever FileItem it was last handed — if it's open
  // on the file a version action just replaced, push the new version into it
  // (and its Previous/Next siblings) so it stops showing the superseded row.
  const refreshPreviewIfShowing = (
    previousFile: FileItem,
    newDocument: ProjectDocument
  ) => {
    if (actions.previewFile?.key !== previousFile.key) return
    const updated = mapDocumentToFileItem(newDocument, rights)
    actions.setPreviewFile(updated)
    setPreviewContext((items) =>
      items.map((item) => (item.key === previousFile.key ? updated : item))
    )
  }

  // A new version's row id is not the id the file was favorited under —
  // carry the favorite forward so uploading/restoring never silently
  // un-favorites the file.
  const handleVersionUploaded = (newVersion: ProjectDocument) => {
    const previousFile = actions.uploadingNewVersionFile
    actions.setUploadingNewVersionFile(null)
    if (previousFile) {
      favorites.migrateFavorite(
        previousFile.key,
        fileItemKey("document", newVersion.id)
      )
      refreshPreviewIfShowing(previousFile, newVersion)
    }
    void data.reload()
  }

  const handleVersionRestored = async (version: ProjectDocument) => {
    const previousFile = actions.versionHistoryFile
    const restored = await actions.restoreVersion(version.id)
    if (restored && previousFile) {
      favorites.migrateFavorite(
        previousFile.key,
        fileItemKey("document", restored.id)
      )
      refreshPreviewIfShowing(previousFile, restored)
    }
    return !!restored
  }

  const handleViewRecord = (record: RelatedRecord) => {
    if (record.type === "proposal") {
      navigate(RoutesPaths.proposal.view(record.id))
    } else if (record.type === "contract") {
      navigate(RoutesPaths.contract.view(record.id))
    } else if (record.type === "invoice") {
      navigate(RoutesPaths.project.view(projectId).invoices)
    } else {
      navigate(RoutesPaths.project.view(projectId).overview)
    }
  }

  const uploadControl = (type: "primary" | "default", label?: string) =>
    createAllowed && (
      <FilesUploadMenu
        type={type}
        label={label}
        onUploadFiles={enqueueUploads}
        onUploadDocument={() => actions.setUploadDocumentOpen(true)}
        onUploadForSignature={
          sendForSignature ? () => sendForSignature() : undefined
        }
      />
    )

  const newFolderControl = (type: "link" | "default") =>
    createAllowed && (
      <Button
        type={type}
        size={type === "link" ? "small" : undefined}
        icon={<FolderAddOutlined />}
        onClick={() => actions.setNewFolderOpen(true)}
      >
        {t("files.actionNewFolder")}
      </Button>
    )

  const newAlbumControl = (type: "link" | "default") =>
    albumCreateAllowed && (
      <Button
        type={type}
        size={type === "link" ? "small" : undefined}
        icon={<PictureOutlined />}
        onClick={() => setIsCreateAlbumOpen(true)}
      >
        {t("files.actionNewAlbum")}
      </Button>
    )

  const previewFrom = (items: FileItem[]) => (file: FileItem) => {
    setPreviewContext(items)
    actions.setPreviewFile(file)
  }

  const renderFileTable = (
    items: FileItem[],
    folders: FolderListItem[] = []
  ) => (
    <FilesListView
      items={items}
      folders={folders}
      folderActions={actions.folderActions}
      onOpenFolder={data.openFolder}
      actions={actions.fileActions}
      selectedKeys={actions.selectedKeys}
      onSelectionChange={actions.setSelection}
      onPreview={previewFrom(items)}
      isFavorite={(file) => favorites.isFavorite(file.key)}
      onToggleFavorite={(file) => favorites.toggleFavorite(file.key)}
    />
  )

  const renderFileItems = (items: FileItem[]) => {
    const onPreview = previewFrom(items)
    return view === "list" ? (
      renderFileTable(items)
    ) : (
      <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(160px,1fr))]">
        {items.map((file) => (
          <FileCard
            key={file.key}
            file={file}
            onPreview={onPreview}
            actions={actions.fileActions(file)}
            selected={actions.selectedKeys.has(file.key)}
            onToggleSelect={actions.toggleSelection}
            draggable={file.canMove && (nav === "folders" || nav === "all")}
            favorited={favorites.isFavorite(file.key)}
            onToggleFavorite={(f) => favorites.toggleFavorite(f.key)}
          />
        ))}
      </div>
    )
  }

  const renderFolderGrid = (
    items: FolderListItem[],
    options?: { showCreateTile?: boolean }
  ) => (
    <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(180px,1fr))]">
      {options?.showCreateTile && createAllowed && (
        <CreateFolderCard onClick={() => actions.setNewFolderOpen(true)} />
      )}
      {items.map((folder) => {
        const canManage = actions.canManageFolder(folder)
        return (
          <FolderCard
            key={folder.id}
            folder={folder}
            onOpen={data.openFolder}
            actions={actions.folderActions(folder)}
            draggable={canManage && nav === "folders"}
            onDropFile={(fileKey) => actions.moveFileById(fileKey, folder.id)}
            onDropFolder={
              canManage
                ? (folderId) => void actions.moveFolderTo(folderId, folder.id)
                : undefined
            }
          />
        )
      })}
    </div>
  )

  const renderFolders = (items: FolderListItem[]) =>
    view === "list" ? renderFileTable([], items) : renderFolderGrid(items)

  const renderLoadMore = () =>
    data.hasMore && (
      <Flex align="center" justify="center" gap="small">
        <Text type="secondary">
          {t("files.showing", {
            shown: data.loadedTotal,
            total: data.overallTotal
          })}
        </Text>
        <Button
          size="small"
          loading={data.isLoadingMore}
          onClick={() => void data.loadMoreDocuments()}
        >
          {t("files.loadMore")}
        </Button>
      </Flex>
    )

  const renderMainContent = () => {
    if (nav === "trash") {
      if (trash.isLoading && !trash.hasLoaded) {
        return (
          <Flex vertical gap="middle">
            <Skeleton.Node className="w-full h-32" />
            <Skeleton.Node className="w-full h-32" />
          </Flex>
        )
      }
      if (trash.isEmpty) {
        return (
          <Card size="small">
            <Empty description={t("files.trash.empty")} />
          </Card>
        )
      }
      return (
        <Flex vertical gap="small">
          <Flex justify="end">
            <Button danger icon={<DeleteOutlined />} onClick={handleEmptyTrash}>
              {t("files.emptyTrash.action")}
            </Button>
          </Flex>
          <FilesListView
            items={trash.fileItems}
            actions={trashFileActions}
            onPreview={(file) => actions.setPreviewFile(file)}
            trashMode
          />
        </Flex>
      )
    }

    if (data.isLoading && !data.hasLoaded) {
      return (
        <Flex vertical gap="middle">
          <Skeleton.Node className="w-full h-32" />
          <Skeleton.Node className="w-full h-32" />
        </Flex>
      )
    }

    if (nav === "favorites") {
      return (
        <FilesSectionCard title={t("files.sidebar.favorites")}>
          {favoriteItems.length ? (
            renderFileItems(favoriteItems)
          ) : (
            <Empty description={t("files.favorites.empty")} />
          )}
        </FilesSectionCard>
      )
    }

    if (isEmptyProject) {
      return (
        <Card size="small">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Flex vertical gap="small" align="center">
                <Text strong>{t("files.empty.title")}</Text>
                <Text type="secondary">{t("files.empty.description")}</Text>
              </Flex>
            }
          >
            <Flex gap="small" justify="center" wrap="wrap">
              {uploadControl("default", t("files.empty.upload"))}
              {newFolderControl("default")}
              {albumCreateAllowed && (
                <Button
                  icon={<PictureOutlined />}
                  onClick={() => setNav("albums")}
                >
                  {t("files.empty.createAlbum")}
                </Button>
              )}
            </Flex>
          </Empty>
        </Card>
      )
    }

    if (isSearching || data.hasActiveFilter) {
      const { folders: resultFolders, albums: resultAlbums } = containersInFilteredResult(
        { folders: sortedFolders, albums: data.albumsPreview },
        data.filters,
        isSearching
      )
      const totalResults =
        sortedFileItems.length + resultFolders.length + resultAlbums.length
      // Some filters are matched in the browser, so a match can sit on a page not loaded yet.
      const noResults = totalResults === 0 && !data.hasMore

      return (
        <Flex vertical gap="middle">
          <Flex align="center" justify="space-between" wrap="wrap" gap="small">
            <Title level={5} className="!mb-0">
              {isSearching
                ? t("files.search.resultsFor", { query: data.search })
                : t("files.search.results")}
            </Title>
            {totalResults > 0 && (
              <Text type="secondary">
                {t("files.search.count", { count: totalResults })}
              </Text>
            )}
          </Flex>
          {noResults ? (
            <Empty
              description={
                isSearching
                  ? t("files.search.noResultsFor", { query: data.search })
                  : t("files.search.noResultsFiltered")
              }
            >
              {isSearching ? (
                <Button onClick={() => data.setSearch("")}>
                  {t("files.search.clear")}
                </Button>
              ) : (
                <Button onClick={() => data.setFilters(EMPTY_FILES_FILTERS)}>
                  {t("files.filters.clearAll")}
                </Button>
              )}
            </Empty>
          ) : (
            <Flex vertical gap="middle">
              {!!resultFolders.length && (
                <FilesSectionCard title={t("files.sidebar.folders")}>
                  {renderFolders(resultFolders)}
                </FilesSectionCard>
              )}
              {!!resultAlbums.length && (
                <FilesSectionCard title={t("files.sidebar.albums")}>
                  <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(220px,1fr))]">
                    {resultAlbums.map((album) => (
                      <AlbumCard
                        key={album.id}
                        album={album}
                        onView={() =>
                          navigate(
                            RoutesPaths.project.view(projectId).album(album.id)
                          )
                        }
                        actions={albumMenuItemsFor(album)}
                      />
                    ))}
                  </div>
                </FilesSectionCard>
              )}
              {!!sortedFileItems.length && (
                <FilesSectionCard title={t("files.sidebar.all")}>
                  {renderFileItems(sortedFileItems)}
                </FilesSectionCard>
              )}
              {renderLoadMore()}
            </Flex>
          )}
        </Flex>
      )
    }

    if (nav === "recent") {
      return (
        <FilesSectionCard title={t("files.sidebar.recent")}>
          {sortedFileItems.length ? (
            <Flex vertical gap="middle">
              {renderFileItems(sortedFileItems)}
              {renderLoadMore()}
            </Flex>
          ) : (
            <Empty description={t("files.recent.empty")} />
          )}
        </FilesSectionCard>
      )
    }

    if (nav === "folders") {
      const isEmptyFolder = !sortedFolders.length && !sortedFileItems.length
      return (
        <Flex vertical gap="middle">
          <Breadcrumb
            items={[
              {
                title: (
                  <a onClick={() => data.jumpToFolderDepth(0)}>
                    {t("files.sidebar.folders")}
                  </a>
                )
              },
              ...(data.isFolderPathComplete
                ? []
                : [
                    {
                      title: (
                        <Tooltip title={t("files.folder.pathIncomplete")}>
                          <span>…</span>
                        </Tooltip>
                      )
                    }
                  ]),
              ...data.folderPath.map((folder, index) => ({
                title: (
                  <a onClick={() => data.jumpToFolderDepth(index + 1)}>
                    {folder.name}
                  </a>
                )
              }))
            ]}
          />
          <Spin spinning={data.isOpeningFolder} delay={FOLDER_LOADER_DELAY_MS}>
            {isEmptyFolder ? (
              <Empty
                description={
                  currentFolderId
                    ? t("files.folder.empty")
                    : t("files.folder.emptyRoot")
                }
              >
                <Flex gap="small" justify="center" wrap="wrap">
                  {uploadControl("default", t("files.empty.upload"))}
                  {newFolderControl("default")}
                </Flex>
              </Empty>
            ) : (
              <Flex vertical gap="middle">
                {view === "list" ? (
                  <>
                    {createAllowed && (
                      <CreateFolderRow onClick={() => actions.setNewFolderOpen(true)} />
                    )}
                    {renderFileTable(sortedFileItems, sortedFolders)}
                  </>
                ) : (
                  <>
                    {renderFolderGrid(sortedFolders, { showCreateTile: true })}
                    {!!sortedFileItems.length && renderFileItems(sortedFileItems)}
                  </>
                )}
                {renderLoadMore()}
              </Flex>
            )}
          </Spin>
        </Flex>
      )
    }

    return (
      <Flex vertical gap="large">
        <FilesSectionCard
          title={t("files.recent.title")}
          action={
            recentItems.length > 0 && (
              <Button type="link" size="small" onClick={() => setNav("recent")}>
                {t("files.viewAll")}
              </Button>
            )
          }
        >
          {recentItems.length ? (
            renderFileItems(recentItems)
          ) : (
            <Empty description={t("files.recent.none")} />
          )}
        </FilesSectionCard>

        <FilesSectionCard
          title={t("files.sidebar.folders")}
          action={
            <Flex gap="small" align="center">
              {newFolderControl("link")}
              <Button
                type="link"
                size="small"
                onClick={() => setNav("folders")}
              >
                {t("files.viewAll")}
              </Button>
            </Flex>
          }
        >
          {sortedFolders.length ? (
            renderFolders(sortedFolders.slice(0, FOLDERS_PREVIEW_LIMIT))
          ) : (
            <Empty description={t("files.folder.emptyRoot")} />
          )}
        </FilesSectionCard>

        <FilesSectionCard
          title={t("files.sidebar.albums")}
          action={
            <Flex gap="small" align="center">
              {newAlbumControl("link")}
              <Button type="link" size="small" onClick={() => setNav("albums")}>
                {t("files.viewAll")}
              </Button>
            </Flex>
          }
        >
          {data.albumsPreview.length ? (
            <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(220px,1fr))]">
              {data.albumsPreview.map((album) => (
                <AlbumCard
                  key={album.id}
                  album={album}
                  onView={() =>
                    navigate(
                      RoutesPaths.project.view(projectId).album(album.id)
                    )
                  }
                  actions={albumMenuItemsFor(album)}
                />
              ))}
            </div>
          ) : (
            <Empty description={t("files.albums.empty")} />
          )}
        </FilesSectionCard>
      </Flex>
    )
  }

  const showListingControls = data.hasListing

  return (
    // Fluid within whatever width the Project workspace already gives this
    // tab — every nav (Recent/Folders/Albums/Favorites/Trash/search/
    // filtered) renders through this one root, so "View all" and the page
    // actions naturally track the same right edge as the content below them
    // without a hard cap. Card/table density is controlled inside each
    // section instead (the folder/album grids' own `minmax()` tracks, the
    // list view's own column widths) — see the per-section grids below.
    <Flex
      vertical
      gap="middle"
      flex={1}
      className={`overflow-hidden w-full min-w-0${dragActive ? " ring-2 ring-primary ring-inset" : ""}`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      data-guide-target="project-files-panel"
    >
      <Flex vertical gap="middle">
        {fromGlobalMap && (
          <Alert
            type="info"
            showIcon
            data-testid="files-from-global-map-banner"
            message={t("files.fromGlobalMap.title")}
            action={
              <Button
                type="primary"
                // antd's built-in `ArrowLeftOutlined` renders its own
                // un-hidden `aria-label="arrow-left"`, which merges into the
                // button's computed accessible name alongside the visible
                // text ("arrow-left Back to Location") — same fix as the
                // Global Map quick view's own Back button.
                aria-label={t("files.backToLocation")}
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(fromGlobalMap)}
              >
                {t("files.backToLocation")}
              </Button>
            }
          />
        )}
        <Flex align="center" justify="space-between" wrap="wrap" gap="small">
          <Flex vertical gap={0}>
            <Title level={4} className="!mb-0">
              {t("files.title")}
            </Title>
            <Paragraph type="secondary" className="!mb-0">
              {t("files.subtitle")}
            </Paragraph>
          </Flex>
          <Flex gap="small">
            {uploadControl("primary")}
            {newFolderControl("default")}
          </Flex>
        </Flex>
      </Flex>

      {showListingControls && (
        <Flex vertical gap="small">
          <Flex gap="small" align="center" wrap="wrap">
            <Input.Search
              allowClear
              value={data.search}
              placeholder={t("files.searchPlaceholder")}
              onChange={(e) => data.setSearch(e.target.value)}
              className="max-w-md"
            />
            <FilterDrawerTrigger
              onClick={() => setFilterDrawerOpen(true)}
              activeCount={activeFilterCount}
            />
          </Flex>
          <FilesActiveFilters
            filters={data.filters}
            labels={filterLabels}
            onChange={data.setFilters}
          />
        </Flex>
      )}

      <Flex gap="middle" flex={1} className="overflow-hidden min-h-0">
        <FilesSidebar
          active={nav}
          onChange={setNav}
          sharedByCustomerActive={isSharedByCustomerActive}
          onSharedByCustomerClick={activateSharedByCustomer}
        />

        <Flex
          vertical
          flex={1}
          gap="middle"
          className="overflow-y-auto min-w-0"
        >
          {showListingControls && (
            <>
              {actions.selectedFiles.length > 0 && (
                <BulkActionBar
                  count={actions.selectedFiles.length}
                  onMove={
                    actions.bulkCanMove
                      ? () => actions.setMovingFiles(actions.selectedFiles)
                      : undefined
                  }
                  onAddToAlbum={
                    actions.bulkHasAttachments && createAllowed
                      ? () =>
                          actions.setAddingToAlbumFiles(actions.selectedFiles)
                      : undefined
                  }
                  onDownload={actions.bulkDownload}
                  onDelete={() => void actions.bulkDelete()}
                  onClear={actions.clearSelection}
                  canDelete={actions.bulkCanDelete}
                  onFavorite={() =>
                    favorites.setFavorites(
                      actions.selectedFiles.map((f) => f.key),
                      true
                    )
                  }
                  onUnfavorite={() =>
                    favorites.setFavorites(
                      actions.selectedFiles.map((f) => f.key),
                      false
                    )
                  }
                  allFavorited={actions.selectedFiles.every((f) =>
                    favorites.isFavorite(f.key)
                  )}
                  statusOptions={
                    actions.bulkCanEditClassification
                      ? actions.bulkAvailableStatusOptions
                      : undefined
                  }
                  statusApplicable={actions.bulkCanEditClassification}
                  statusesFailed={actions.statusesFailed}
                  onChangeStatus={(value) =>
                    void actions.bulkChangeStatus(actions.selectedFiles, value)
                  }
                  documentTypeOptions={
                    actions.bulkCanEditClassification
                      ? actions.documentTypeOptions
                      : undefined
                  }
                  onChangeDocumentType={(value) =>
                    void actions.bulkChangeDocumentType(
                      actions.selectedFiles,
                      value
                    )
                  }
                />
              )}
              <Flex justify="end" align="center" gap="small">
                <FilesSortSelect value={sortOrder} onChange={setSortOrder} />
                <Radio.Group
                  value={view}
                  onChange={(e) => setView(e.target.value)}
                  optionType="button"
                  options={[
                    {
                      label: (
                        <span aria-label={t("files.view.grid")}>
                          <AppstoreOutlined />
                        </span>
                      ),
                      value: "grid"
                    },
                    {
                      label: (
                        <span aria-label={t("files.view.list")}>
                          <BarsOutlined />
                        </span>
                      ),
                      value: "list"
                    }
                  ]}
                />
              </Flex>
            </>
          )}

          {nav === "albums" ? (
            <ProjectViewAlbumsSection
              onCreateAlbumClick={() => setIsCreateAlbumOpen(true)}
            />
          ) : (
            renderMainContent()
          )}
        </Flex>
      </Flex>

      <FilesFilterDrawer
        open={filterDrawerOpen}
        filters={data.filters}
        hideFolderFilter={!!currentFolderId}
        projectId={projectId}
        projectName={projectName}
        owners={owners}
        onClose={() => setFilterDrawerOpen(false)}
        onChange={data.setFilters}
        onOptionsLoaded={setFilterLabels}
      />

      {actions.previewFile && (
        <FileViewer
          file={actions.previewFile}
          items={previewContext.length ? previewContext : [actions.previewFile]}
          onClose={() => actions.setPreviewFile(null)}
          fileActions={actions.fileActions}
          isFavorite={(f) => favorites.isFavorite(f.key)}
          onToggleFavorite={(f) => favorites.toggleFavorite(f.key)}
          onRename={(f) => actions.setRenamingFile(f)}
          onMove={(f) => actions.setMovingFiles([f])}
          onAddToAlbum={(f) => actions.setAddingToAlbumFiles([f])}
          documentTypeOptions={actions.documentTypeOptions}
          documentTypesFailed={actions.documentTypesFailed}
          onChangeDocumentType={actions.updateDocumentType}
          onLinkRecord={(f) => actions.setLinkingFile(f)}
          onViewRecord={handleViewRecord}
          onUploadNewVersion={(f) => actions.setUploadingNewVersionFile(f)}
          onViewVersionHistory={(f) => actions.setVersionHistoryFile(f)}
          statusOptions={actions.statusOptions}
          statusesFailed={actions.statusesFailed}
          onChangeStatus={actions.updateStatus}
          onRequestReview={(f) => actions.setRequestReviewFile(f)}
          onViewReview={(f) => actions.setReviewingFile(f)}
          onViewComments={(f) => actions.setCommentsFile(f)}
          onViewActivity={(f) => actions.setActivityFile(f)}
          onShare={(f) => actions.setSharingFile(f)}
          onToggleCustomerVisibility={actions.updateCustomerVisibility}
        />
      )}

      {actions.sharingFile && (
        <ShareDialog
          file={actions.sharingFile}
          onClose={() => actions.setSharingFile(null)}
        />
      )}

      {actions.linkingFile && (
        <LinkRecordModal
          projectId={projectId}
          projectName={projectName}
          currentRecord={actions.linkingFile.relatedRecord ?? null}
          onClose={() => actions.setLinkingFile(null)}
          onSubmit={actions.linkRecord}
          onUnlink={actions.unlinkRecord}
        />
      )}

      {actions.uploadingNewVersionFile && (
        <UploadNewVersionModal
          projectId={projectId}
          file={actions.uploadingNewVersionFile}
          onClose={() => actions.setUploadingNewVersionFile(null)}
          onUploaded={handleVersionUploaded}
        />
      )}

      {actions.versionHistoryFile && (
        <VersionHistoryDrawer
          open
          fileName={actions.versionHistoryFile.name}
          documentId={actions.versionHistoryFile.id}
          onClose={() => actions.setVersionHistoryFile(null)}
          onPreviewVersion={handlePreviewVersion}
          onRestore={handleVersionRestored}
        />
      )}

      {actions.newFolderOpen && (
        <NewFolderModal
          onClose={() => actions.setNewFolderOpen(false)}
          onSubmit={actions.createFolderHere}
        />
      )}

      {isCreateAlbumOpen && (
        <CreateAlbumModal
          onClose={() => setIsCreateAlbumOpen(false)}
          onSubmit={handleCreateAlbum}
        />
      )}

      {actions.uploadDocumentOpen && (
        <UploadDocumentModal
          projectId={projectId}
          folderId={currentFolderId}
          onClose={() => actions.setUploadDocumentOpen(false)}
          onUploaded={() => void data.reload()}
        />
      )}

      {actions.renamingFolder && (
        <NewFolderModal
          title={t("files.rename.folderTitle")}
          initialName={actions.renamingFolder.name}
          onClose={() => actions.setRenamingFolder(null)}
          onSubmit={actions.renameFolder}
        />
      )}

      {actions.movingFiles && actions.movingFiles.length > 0 && (
        <MoveFileModal
          projectId={projectId}
          files={actions.movingFiles}
          onClose={() => actions.setMovingFiles(null)}
          onSubmit={(folderId) =>
            actions.movingFiles?.length === 1
              ? actions.moveFile(actions.movingFiles[0], folderId)
              : actions.bulkMove(actions.movingFiles ?? [], folderId)
          }
        />
      )}

      {actions.movingFolder && (
        <MoveFolderModal
          folder={actions.movingFolder}
          folders={actions.allFoldersForMove}
          onClose={() => actions.setMovingFolder(null)}
          onSubmit={(parentId) =>
            actions.moveFolderTo(actions.movingFolder?.id ?? "", parentId)
          }
        />
      )}

      {actions.renamingFile && (
        <RenameFileModal
          fileName={actions.renamingFile.name}
          onClose={() => actions.setRenamingFile(null)}
          onSubmit={(next) =>
            actions.renameFile(actions.renamingFile as FileItem, next)
          }
        />
      )}

      {actions.assigningDocumentTypeFile && (
        <AssignDocumentTypeModal
          file={actions.assigningDocumentTypeFile}
          options={actions.documentTypeOptions}
          loadFailed={actions.documentTypesFailed}
          onClose={() => actions.setAssigningDocumentTypeFile(null)}
          onSubmit={(value) =>
            actions.updateDocumentType(
              actions.assigningDocumentTypeFile as FileItem,
              value
            )
          }
        />
      )}

      <ProjectFileAddToAlbumModal
        open={!!actions.addingToAlbumFiles}
        onClose={() => actions.setAddingToAlbumFiles(null)}
        onSave={(albumId) => {
          void actions.addToAlbum(albumId)
          actions.setAddingToAlbumFiles(null)
        }}
      />

      {actions.requestReviewFile && (
        <RequestReviewModal
          file={actions.requestReviewFile}
          onClose={() => actions.setRequestReviewFile(null)}
          onSubmit={actions.submitRequestReview}
        />
      )}

      {actions.reviewingFile && (
        <ReviewDrawer
          file={actions.reviewingFile}
          refreshKey={actions.reviewRefreshKey}
          onClose={() => actions.setReviewingFile(null)}
          onOpenDocument={() => {
            const file = actions.reviewingFile
            if (!file) return
            setPreviewContext([file])
            actions.setPreviewFile(file)
          }}
          onAddComment={() => {
            const file = actions.reviewingFile
            if (file) actions.setCommentsFile(file)
          }}
          onApprove={actions.approveReview}
          onReject={(review) => actions.setRejectingReview(review)}
          onRequestChanges={(review) =>
            actions.setRequestingChangesReview(review)
          }
        />
      )}

      {actions.rejectingReview && actions.reviewingFile && (
        <RejectReviewModal
          fileName={actions.reviewingFile.name}
          onClose={() => actions.setRejectingReview(null)}
          onSubmit={actions.rejectReview}
        />
      )}

      {actions.requestingChangesReview && actions.reviewingFile && (
        <RejectReviewModal
          fileName={actions.reviewingFile.name}
          onClose={() => actions.setRequestingChangesReview(null)}
          onSubmit={actions.requestChanges}
          danger={false}
          title={t("files.review.requestChangesTitle")}
          submitText={t("files.review.requestChangesSubmit")}
          reasonLabel={t("files.review.requestChangesReason")}
          placeholder={t("files.review.requestChangesPlaceholder")}
        />
      )}

      {actions.commentsFile && (
        <CommentsDrawer
          file={actions.commentsFile}
          onClose={() => actions.setCommentsFile(null)}
        />
      )}

      {actions.activityFile && (
        <ActivityDrawer
          file={actions.activityFile}
          onClose={() => actions.setActivityFile(null)}
        />
      )}

      {uploadQueueOpen && (
        <UploadQueuePanel
          items={uploadQueue.items}
          onRetry={uploadQueue.retry}
          onCancelOne={uploadQueue.cancelOne}
          onCancelAll={uploadQueue.cancelAll}
          onClose={() => setUploadQueueOpen(false)}
        />
      )}

      {albumActionsController.modals}
    </Flex>
  )
}

export default ProjectViewFilesSection
